import { add } from "date-fns";
import { FieldValues } from "react-hook-form";
import { MaskOperationModeEnum, MaskType, PlanningModeEnum } from "shared/enums/globalEnums";
import {
  AfrrPlanning,
  GroupKey,
  InitialFormValues,
  JBoxTypeKey,
  PlanningConfig,
  RampTestKey,
  SocManagementPlanning,
} from "./types";

export const isTestMasks = (masks: MaskType[]): boolean =>
  masks.every((mask) => mask.operating_mode === MaskOperationModeEnum.r2_chronicles);

export const PLANNING_CONFIGS: PlanningConfig[] = [
  {
    durationInMinutes: 60,
    rampKey: RampTestKey.RampTest1,
    planningsKey: GroupKey.PlanningsSocManagement,
  },
  {
    durationInMinutes: 15,
    rampKey: RampTestKey.RampTest1,
    planningsKey: GroupKey.PlanningsAfrrPhase1,
  },
  {
    durationInMinutes: 20,
    rampKey: RampTestKey.RampTest1,
    planningsKey: GroupKey.PlanningsAfrrPhase2,
  },
  {
    durationInMinutes: 55,
    rampKey: RampTestKey.RampTest2,
    planningsKey: GroupKey.PlanningsSocManagement,
  },
  {
    durationInMinutes: 15,
    rampKey: RampTestKey.RampTest2,
    planningsKey: GroupKey.PlanningsAfrrPhase1,
  },
  {
    durationInMinutes: 20,
    rampKey: RampTestKey.RampTest2,
    planningsKey: GroupKey.PlanningsAfrrPhase2,
  },
  {
    durationInMinutes: 55,
    rampKey: null,
    planningsKey: GroupKey.PlanningsSocManagement,
  },
];

const createSocManagementPlanning = (
  start_date: Date,
  duration: number,
  target_soc: number,
): SocManagementPlanning => ({
  start_date,
  end_date: add(start_date, { minutes: duration }),
  mode: PlanningModeEnum.soc_management,
  target_soc,
  overridable: false,
});

const createAfrrPlanning = (
  start_date: Date,
  duration: number,
  initial_soc_restoration_power: number,
  afrr_engagement_up: number,
  afrr_engagement_down: number,
  reactivity_delay: number,
): AfrrPlanning => ({
  mode: PlanningModeEnum.afrr_rte,
  start_date,
  end_date: add(start_date, { minutes: duration }),
  initial_soc_restoration_power,
  afrr_engagement_up,
  afrr_engagement_down,
  overridable: false,
  reactivity_delay,
});

const initializeResultBuckets = (): Record<JBoxTypeKey, Array<SocManagementPlanning | AfrrPlanning>> => ({
  jbox8racks: [],
  jbox10racks: [],
  jbox2: [],
});

const initializeLastEndDates = (startDate: Date): Record<JBoxTypeKey, Date> => ({
  jbox8racks: startDate,
  jbox10racks: startDate,
  jbox2: startDate,
});

const handleSocManagementPlanning = (
  planningBlock: { target_soc: Record<JBoxTypeKey, number>; duration: number },
  rack: JBoxTypeKey,
  start: Date,
  durationInMinutes: number,
): SocManagementPlanning => {
  const target = planningBlock.target_soc[rack];
  const selectedDuration = planningBlock.duration || durationInMinutes;
  return createSocManagementPlanning(start, selectedDuration, target);
};

const handleAfrrPlanning = (
  planningBlock: {
    initial_soc_restoration_power: Record<JBoxTypeKey, number>;
    afrr_engagement_up: Record<JBoxTypeKey, number>;
    afrr_engagement_down: Record<JBoxTypeKey, number>;
    reactivity_delay: number;
  },
  rack: JBoxTypeKey,
  start: Date,
  durationInMinutes: number,
  thisAfrrIndex: number,
): AfrrPlanning => {
  const rawInitial = planningBlock.initial_soc_restoration_power[rack];
  const initialPower = thisAfrrIndex === undefined || thisAfrrIndex === null ? 0 : rawInitial;
  return createAfrrPlanning(
    start,
    durationInMinutes,
    initialPower,
    planningBlock.afrr_engagement_up[rack],
    planningBlock.afrr_engagement_down[rack],
    planningBlock.reactivity_delay,
  );
};

const appendFinalSocManagement = (
  result: Record<JBoxTypeKey, Array<SocManagementPlanning | AfrrPlanning>>,
  lastEnd: Record<JBoxTypeKey, Date>,
  finalConfig: PlanningConfig,
): void => {
  (Object.values(JBoxTypeKey) as JBoxTypeKey[]).forEach((rack) => {
    const start = lastEnd[rack];
    const finalPlanning = createSocManagementPlanning(start, finalConfig.durationInMinutes, 0.5);
    result[rack].push(finalPlanning);
  });
};

export const generateRackPlannings = (
  initialValues: InitialFormValues,
  configs: PlanningConfig[],
): Record<JBoxTypeKey, Array<SocManagementPlanning | AfrrPlanning>> => {
  if (!initialValues.startingDate) {
    return initializeResultBuckets();
  }

  const result = initializeResultBuckets();
  const lastEnd = initializeLastEndDates(new Date(initialValues.startingDate));
  const afrrCount: Record<string, number> = {};

  configs.forEach((config) => {
    if (!config.rampKey) return;
    const rampBlock = initialValues[config.rampKey];
    if (!rampBlock) return;

    const isSoc = config.planningsKey === GroupKey.PlanningsSocManagement;

    // ✅ new: SoC uses its own active; AFRR phases share rampBlock.afrr_active
    const isActive = isSoc
      ? Boolean((rampBlock as any)[GroupKey.PlanningsSocManagement]?.active)
      : Boolean(rampBlock.afrr_active);

    if (!isActive) return;

    // pull the block values (no 'active' inside AFRR blocks anymore)
    const planningBlock = (rampBlock as any)[config.planningsKey];
    if (!planningBlock) return;

    let thisAfrrIndex = 0;
    if (!isSoc) {
      afrrCount[config.rampKey] = (afrrCount[config.rampKey] || 0) + 1;
      thisAfrrIndex = afrrCount[config.rampKey];
    }

    (Object.values(JBoxTypeKey) as JBoxTypeKey[]).forEach((rack) => {
      const start = lastEnd[rack];

      const planning = isSoc
        ? handleSocManagementPlanning(
            planningBlock as { target_soc: Record<JBoxTypeKey, number>; duration: number },
            rack,
            start,
            config.durationInMinutes,
          )
        : handleAfrrPlanning(
            planningBlock as {
              initial_soc_restoration_power: Record<JBoxTypeKey, number>;
              afrr_engagement_up: Record<JBoxTypeKey, number>;
              afrr_engagement_down: Record<JBoxTypeKey, number>;
              reactivity_delay: number;
            },
            rack,
            start,
            config.durationInMinutes,
            thisAfrrIndex,
          );

      result[rack].push(planning);
      lastEnd[rack] = planning.end_date;
    });
  });

  const finalConfig = configs.find((c) => c.rampKey === null && c.planningsKey === "plannings_soc_management");
  if (finalConfig) {
    appendFinalSocManagement(result, lastEnd, finalConfig);
  }

  return result;
};

export const getJBoxTypeFromRackCount = (rackCount: number) => {
  switch (rackCount) {
    case 10:
      return JBoxTypeKey.JBox10Racks;
    case 6:
      return JBoxTypeKey.JBox2;
    case 8:
    default:
      return JBoxTypeKey.JBox8Racks;
  }
};

export const preparePlannings = (
  planningsByRack: Record<JBoxTypeKey, Array<SocManagementPlanning | AfrrPlanning>>,
  sites: { id: number; rack_count: number }[],
) => {
  return sites.reduce((acc, site) => {
    const jBoxType = getJBoxTypeFromRackCount(site.rack_count);
    return {
      ...acc,
      [site.id]: [...planningsByRack[jBoxType]],
    };
  }, {});
};

export const formatIecConfigs = (
  { rampTest1, rampTest2, group_id }: FieldValues,
  planningsByRack: Record<JBoxTypeKey, Array<SocManagementPlanning | AfrrPlanning>>,
) => {
  const afrrTest1Active = Boolean(rampTest1?.afrr_active);
  const afrrTest2Active = Boolean(rampTest2?.afrr_active);

  // all racks share same timeline structure; pick any rack bucket
  const afrrPlannings = planningsByRack.jbox8racks.filter((p) => p.mode === PlanningModeEnum.afrr_rte);

  const configs: any[] = [];
  const baseConfig = { group_id, type: "nlevel_simulation" as const };

  if (afrrTest1Active) {
    configs.push({
      ...baseConfig,
      start_date: afrrPlannings[0]?.start_date,
      end_date: afrrPlannings[1]?.end_date,
      test_file_path: rampTest1?.chronicle_filename,
    });
  }

  if (afrrTest2Active) {
    const startIndex = afrrTest1Active ? 2 : 0;
    configs.push({
      ...baseConfig,
      start_date: afrrPlannings[startIndex]?.start_date,
      end_date: afrrPlannings[startIndex + 1]?.end_date,
      test_file_path: rampTest2?.chronicle_filename,
    });
  }

  return configs;
};

export const getChronicleFilenameChoices = (confs: string[] | null) => {
  if (!confs) return [];
  return confs.map((conf) => ({
    id: conf,
    name: conf,
  }));
};
